import { useEffect, useMemo, useState } from "react";
import { SegmentKind } from "@/services/backend/vbs/treatments/types/segmentKind";
import { Position } from "@/services/backend/vbs/treatments/types/position";
import { Segment } from "@/services/backend/vbs/treatments/types/segment";
import t from "@/lang/lang";
import {
  useVbsTreatmentAddAreaMutation,
  useVbsTreatmentShowQuery,
} from "@/services/backend/vbs/treatments/api/treatment-api";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { SelectColumnsDropdown } from "@/services/backend/vbs/treatments/components/select-columns-dropdown";
import { useTreatmentComponentState } from "@/services/backend/vbs/treatments/components/treatment-component-state-context";
import { Button } from "@/shared/components/ui/button";
import { ChevronDown, Plus } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Column } from "@/services/backend/vbs/treatments/lib/treatment-component-state";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { CycleDiscountAllCheckbox } from "@/services/backend/vbs/treatments/components/cycle-discount-all-checkbox";
import { fCleanTime } from "@/services/backend/vbs/treatments/lib/utils";
import { SegmentAreaComponent } from "./segment-area-component";

export function SegmentComponent({
  treatmentId,
  segment,
  positions,
}: {
  treatmentId: string;
  segment: Segment;
  positions: Position[];
}) {
  const { segmentKind, areas, cleanTime } = segment;
  const { state, disabled } = useTreatmentComponentState();
  const { columns } = state;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-4">
        <CycleDiscountAllCheckbox
          treatmentId={treatmentId}
          positions={positions}
          disabled={disabled}
        />
        <div className="flex items-center gap-2">
          {!disabled && (
            <AddAreaPopover
              treatmentId={treatmentId}
              segmentKind={segmentKind}
            />
          )}
          <SelectColumnsDropdown />
        </div>
      </div>
      <div>
        {areas.map((segmentArea) => (
          <SegmentAreaComponent
            treatmentId={treatmentId}
            segmentKind={segmentKind}
            segmentArea={segmentArea}
            positions={positions}
            key={segmentArea.area}
          />
        ))}
        {columns.includes(Column.CleanTime) && (
          <div className="bg-muted flex items-center justify-end space-x-4 border-t px-6 py-3 font-bold uppercase">
            <span>{t("Gesamt")}</span>
            <span>{fCleanTime(cleanTime)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function AddAreaPopover({
  treatmentId,
  segmentKind,
}: {
  treatmentId: string;
  segmentKind: SegmentKind;
}) {
  const [open, setOpen] = useState(false);
  const [individualName, setIndividualName] = useState("");
  const [add, { isLoading, error }] = useVbsTreatmentAddAreaMutation();
  const { data: treatment } = useVbsTreatmentShowQuery({ id: treatmentId });
  const { toast } = useToast();

  const { setPersistenceState } = usePersistenceContext();
  useEffect(() => {
    if (isLoading) {
      setPersistenceState(PersistenceState.SAVING);
    } else if (error) {
      setPersistenceState(PersistenceState.ERROR);
    } else {
      setPersistenceState(PersistenceState.SAVED);
    }
  }, [isLoading, error, setPersistenceState]);

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const onAdd = (name: string) => {
    if (isLoading) {
      return;
    }
    add({
      id: treatmentId,
      area: name,
      segment: segmentKind,
    });
    setOpen(false);
  };

  const suggestions = useMemo(() => {
    return [
      // Set deduplicates values
      ...new Set([
        // common areas that are always suggested
        "Hauptküche",
        "Spülküche",
        "Speiseausgabe",
        // add areas that where manually added
        ...(treatment?.positions.map((p) => p.area) ?? []),
      ]),
    ];
  }, [treatment]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <span>{t("Bereich hinzufügen")}</span>
          <ChevronDown className="ml-2 h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex flex-col gap-2">
        <Input
          value={individualName}
          onChange={(e) => setIndividualName(e.target.value)}
          placeholder={t("Name eingeben")}
        />
        <Button
          variant="secondary"
          className="w-full py-2"
          onClick={() => onAdd(individualName)}
        >
          {t("Erstellen")}
        </Button>
        <Separator />
        <Label>{t("Vorschläge")}</Label>
        {suggestions.map((name) => (
          <Button
            key={name}
            onClick={() => onAdd(t(name))}
            className="w-full cursor-pointer"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            <span>{t(name)}</span>
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
